////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.1                                                            //
//  Summary: Typed accessors + structural parsers over the generated content bundle //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

// Thin lookup layer over src/content/ (generated at build time by
// scripts/build-content.ts — see 1.0.1.md §3.4). No runtime fetch, no browser-side
// markdown parsing of REMOTE data: the data is already baked into the bundle. Pages
// consume THIS module, never src/content/* directly, so the generated barrel can be
// regenerated or reshaped without every call site changing.
//
// 1.0.2 addition: the generator (out of this phase's scope, gui/scripts/) emits one
// flat `bodyMd` string per page — it does not know about "Capabilities has six
// image/paragraph pairs" or "Home has six project teaser cards", because that shape
// is specific to two pages, not a general content model. Rather than duplicating the
// ALREADY-SCRAPED copy as a second hand-typed literal in a page component (which
// drifts the moment the scrape is re-run), the parsers below read the fixed,
// deterministic markdown patterns those sections use and turn them into typed
// records. Every parser here is intentionally narrow — it exists because a specific
// page in this phase needs it, not as a general markdown-to-structure engine.

import { news, pages, type NewsArticle, type ScrapedPage } from '@/content';

export function getPage (slug: string): ScrapedPage | undefined {
  return pages.find((p) => p.slug === slug);
}

export function listNews (): readonly NewsArticle[] {
  return news;
}

export function getNewsBySlug (slug: string): NewsArticle | undefined {
  return news.find((n) => n.slug === slug);
}

// ---------------------------------------------------------------------------
// Section splitting
// ---------------------------------------------------------------------------

export interface ContentSection {
  /** Anchor-safe id, derived from `heading`. `'intro'` for content preceding the
   * page's first `#### ` heading (hero image, lead copy). */
  id: string;
  /** Empty string for the pre-heading intro section. */
  heading: string;
  bodyMd: string;
}

const SECTION_HEADING_RE = /^####\s+(.+)$/;

function slugify (value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '') || 'intro';
}

/** Splits a page's `bodyMd` on level-4 (`#### `) headings — the one heading level
 * every scraped static page (home/about/contact/legal) uses to divide its named
 * sections. Content before the first heading comes back as `{ id: 'intro', heading: '' }`. */
export function splitSections (bodyMd: string): ContentSection[] {
  const lines = bodyMd.split(/\r?\n/);
  const sections: ContentSection[] = [];
  let currentId = 'intro';
  let currentHeading = '';
  let buffer: string[] = [];

  const flush = () => {
    const body = buffer.join('\n').trim();
    if (currentHeading !== '' || body !== '') {
      sections.push({ id: currentId, heading: currentHeading, bodyMd: body });
    }
    buffer = [];
  };

  for (const line of lines) {
    const match = SECTION_HEADING_RE.exec(line.trim());
    if (match) {
      flush();
      currentHeading = match[1] ?? '';
      currentId = slugify(currentHeading);
    } else {
      buffer.push(line);
    }
  }
  flush();

  return sections;
}

/** Convenience wrapper — the single section on `slug` whose heading matches exactly. */
export function getSection (slug: string, heading: string): ContentSection | undefined {
  const page = getPage(slug);
  if (!page) return undefined;
  return splitSections(page.bodyMd).find((s) => s.heading === heading);
}

// ---------------------------------------------------------------------------
// Image extraction
// ---------------------------------------------------------------------------

export interface ImageRef {
  src: string;
  /** Verbatim `alt` text captured in the markdown — every scraped page uses bare
   * `![]()` with no alt, so this is almost always `''`. Callers supply meaningful
   * alt text themselves; see components/content/Markdown.tsx `getImageAlt`. */
  alt: string;
}

const IMAGE_RE = /!\[([^\]]*)\]\(([^)]+)\)/g;
const IMAGE_LINE_RE = /^!\[([^\]]*)\]\(([^)]+)\)$/;

/** Every markdown image reference in `bodyMd`, in document order. */
export function extractImages (bodyMd: string): ImageRef[] {
  return [...bodyMd.matchAll(IMAGE_RE)].map((m) => ({ alt: m[1] ?? '', src: m[2] ?? '' }));
}

/** `bodyMd` with every markdown image reference removed — for sections where the
 * image is pulled out and rendered separately (hero, profile portrait) and the
 * remaining prose is rendered on its own. */
export function stripImages (bodyMd: string): string {
  return bodyMd.replace(IMAGE_RE, '').trim();
}

// ---------------------------------------------------------------------------
// Home — "Discover our projects" teaser cards
// ---------------------------------------------------------------------------

export interface ProjectTeaser {
  image: string;
  name: string;
  suburb: string;
  client: string;
  status: string;
}

/** Parses the fixed 6-line-per-card pattern in home.md's "Discover our projects"
 * section: image, name, suburb, client, status, then the literal text "View
 * Project" (which carries no href in the scrape — there is no project detail page
 * behind it yet, hence the placeholder treatment on the Home route). */
export function parseProjectTeasers (sectionBodyMd: string): ProjectTeaser[] {
  const blocks = sectionBodyMd.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  const teasers: ProjectTeaser[] = [];

  for (let i = 0; i + 5 < blocks.length; i += 6) {
    const imageMatch = IMAGE_LINE_RE.exec(blocks[i] ?? '');
    if (!imageMatch) continue;
    teasers.push({
      image: imageMatch[2] ?? '',
      name: blocks[i + 1] ?? '',
      suburb: blocks[i + 2] ?? '',
      client: blocks[i + 3] ?? '',
      status: blocks[i + 4] ?? '',
    });
  }
  return teasers;
}

// ---------------------------------------------------------------------------
// About — Capabilities image/paragraph pairs
// ---------------------------------------------------------------------------

export interface ImageTextPair {
  image: string;
  text: string;
}

/** Parses the alternating image → paragraph(s) → image → … pattern used by
 * about.md's Capabilities section. */
export function parseImageTextPairs (sectionBodyMd: string): ImageTextPair[] {
  const blocks = sectionBodyMd.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  const pairs: ImageTextPair[] = [];
  let pendingImage: string | null = null;
  let textParts: string[] = [];

  const flush = () => {
    if (pendingImage) pairs.push({ image: pendingImage, text: textParts.join('\n\n') });
    textParts = [];
  };

  for (const block of blocks) {
    const single = IMAGE_LINE_RE.exec(block);
    if (single) {
      flush();
      pendingImage = single[2] ?? '';
    } else {
      textParts.push(block);
    }
  }
  flush();
  return pairs;
}

// ---------------------------------------------------------------------------
// About — Managing Director profile
// ---------------------------------------------------------------------------

export interface ProfileSection {
  name: string;
  image: ImageRef | null;
  /** Bio copy with the name subheading and portrait image lines stripped out —
   * render through <Markdown> for the remaining paragraphs. */
  bioMd: string;
}

const SUBHEADING_RE = /^#####\s+(.+)$/;

/** Parses the "##### <Name>" + portrait image + bio-paragraphs pattern used by
 * about.md's Managing Director section. */
export function parseProfileSection (sectionBodyMd: string): ProfileSection {
  const lines = sectionBodyMd.split(/\r?\n/);
  let name = '';
  const remaining: string[] = [];

  for (const line of lines) {
    const heading = SUBHEADING_RE.exec(line.trim());
    if (heading) {
      name = heading[1] ?? '';
      continue;
    }
    remaining.push(line);
  }

  const rest = remaining.join('\n');
  const images = extractImages(rest);
  const bioMd = rest.replace(IMAGE_RE, '').trim();
  return { name, image: images[0] ?? null, bioMd };
}
